import { useEffect, useState, type FormEvent } from "react";

import { api, errorMessage } from "../api/client";
import { ui } from "../lib/ui";
import type { PcStore } from "../types";

type Mode = "hub" | "local";

/** Adds a store to this PC and switches to it.
 *
 *  With a hub configured, the installer's Hub page done from the console:
 *  paste the token issued for the store on the hub's Fleet page; the hub
 *  says which store it is and this PC starts reporting as it (a token for a
 *  store the PC already knows just replaces its token). Without a hub, or
 *  by choice, a **local** store: a name is all it needs — its plan, cameras
 *  and counts stay on this PC. */
export function AddStoreDialog({
  hubUrl,
  onClose,
  onAdded,
}: {
  hubUrl: string | null;
  onClose: () => void;
  onAdded: (store: PcStore) => void;
}) {
  const [mode, setMode] = useState<Mode>(hubUrl ? "hub" : "local");
  const [token, setToken] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const store = await api.addPcStore(
        mode === "hub" ? { token: token.trim() } : { name: name.trim() },
      );
      onAdded(store);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const hub = mode === "hub";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-store-title"
        onSubmit={submit}
        className={`${ui.card} flex w-full max-w-md flex-col gap-4 p-6`}
      >
        <div>
          <h2 id="add-store-title" className="m-0 text-base font-semibold">
            {hub ? "Add a store from the hub" : "Add a local store"}
          </h2>
          <p className="mt-1 text-[13px] text-muted">
            {hub
              ? "Paste the token issued for the store on the hub's Fleet page. The hub says which store it is, and this PC switches to it and starts reporting as it."
              : "A store that lives only on this PC: its floor plan, cameras and counts are kept here and never sent to a hub."}
          </p>
        </div>

        {error && <p className={`${ui.notice} ${ui.noticeError} mb-0`}>{error}</p>}

        {hub ? (
          <label className={ui.field}>
            Branch token
            <input
              className={`${ui.input} font-mono`}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="ez_…"
              autoComplete="off"
              spellCheck={false}
              required
              minLength={8}
              autoFocus
            />
            <span className="font-normal text-muted">Hub: {hubUrl}</span>
          </label>
        ) : (
          <label className={ui.field}>
            Store name
            <input
              className={ui.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Maadi test store"
              required
              maxLength={120}
              autoFocus
            />
          </label>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" className={ui.btn} disabled={busy}>
            {busy ? (hub ? "Checking with the hub…" : "Creating…") : hub ? "Connect" : "Create"}
          </button>
          <button type="button" className={ui.btnQuiet} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {hubUrl && (
            <button
              type="button"
              className="ml-auto cursor-pointer border-0 bg-transparent p-0 text-[13px] text-accent hover:underline"
              onClick={() => {
                setMode(hub ? "local" : "hub");
                setError(null);
              }}
              disabled={busy}
            >
              {hub ? "Add a local store instead" : "Use a hub token instead"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
